import { memo, useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';

import { IndicatorDriver } from 'models/response/driver.interface';
import { getBlockDriversData } from 'redux/PlanPageSlice';

import EditDriversSection from '../../Plan/EditDriverSection';
import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  PLAN_WHATIFS_PANEL_WIDTH,
  accent,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

interface Properties {
  onToggleAuthModal: () => void;
  onClose: () => void;
}

const PlanWhatIfsPanel = ({ onToggleAuthModal, onClose }: Properties) => {
  const blockDrivers = useSelector(getBlockDriversData);

  const indicatorDrivers = useMemo(
    () =>
      (blockDrivers ?? []).filter(
        (driver: IndicatorDriver) => driver.type === 'indicator',
      ) as IndicatorDriver[],
    [blockDrivers],
  );

  if (indicatorDrivers.length === 0) {
    return null;
  }

  return (
    <Flex
      direction='column'
      h='100%'
      minH={0}
      w={PLAN_WHATIFS_PANEL_WIDTH}
      minW={PLAN_WHATIFS_PANEL_WIDTH}
      flexShrink={0}
      bg={neutral.surface}
      borderLeft='1px solid'
      borderColor={neutral.border}
      overflow='hidden'
    >
      <Flex
        alignItems='center'
        gap={2}
        px={PLAN_HEADER_HORIZONTAL_PADDING}
        pt='12px'
        pb='12px'
        flexShrink={0}
      >
        <Flex
          as='button'
          type='button'
          aria-label='Close what ifs panel'
          alignItems='center'
          justifyContent='center'
          h='28px'
          w='28px'
          minW='28px'
          flexShrink={0}
          border='1px solid'
          borderColor={neutral.border}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          bg={neutral.surface}
          color={textToken.secondary}
          cursor='pointer'
          transition='background 0.12s ease, border-color 0.12s ease, color 0.12s ease'
          _hover={{
            bg: neutral.surfaceHover,
            borderColor: neutral.borderStrong,
            color: accent.solid,
          }}
          onClick={onClose}
        >
          <ChevronRightIcon boxSize={4} />
        </Flex>
        <Text {...PLAN_TEXT.inputsPanelTitle} color={textToken.primary}>
          What Ifs
        </Text>
      </Flex>

      <Flex flex='1' minH={0} direction='column' overflowY='auto'>
        <EditDriversSection
          variant='revamp'
          hideHeader
          embeddedInWhatIfsPanel
          drivers={indicatorDrivers}
          onToggleAuthModal={onToggleAuthModal}
        />
      </Flex>
    </Flex>
  );
};

export default memo(PlanWhatIfsPanel);
